/**
 * The picture area of a saved estimate made in the 3D designer: the quick
 * still at first, then — polling the render job — the photoreal pictures as
 * they land. The first finished picture crossfades in on its own (unless the
 * user has already picked one); the rest on a click in the strip. The 360
 * opens in the panorama viewer in place of the still, with a callout on every
 * door, window and garage door; "Back to photos" (or any still in the strip)
 * fades it out again — onto the still asked for, swapped in under the 360
 * before it fades, so the one shown before it never flashes up.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { FloorPlan } from '../../../calculations/plan/floorPlan';
import type { EstimatePreview } from '../../../types';
import { formatFeetInches } from '../../../utils/dimensions';
import { openingMarkers, type OpeningMarker } from './panoMarkers';
import { PanoViewer, type PanoLabels } from './PanoViewer';
import { RenderGallery } from './RenderGallery';
import { RenderProgress, durationText } from './RenderProgress';
import { PICTURE_ORDER, type PictureId, type RenderPicture } from './types';
import { useRenderStatus, type RenderStatusFetcher } from './useRenderStatus';
import { usePhoneSized } from '../../designer/chrome/usePhoneSized';
import type { WaitEffect } from '../cardStyle';
import './render.css';

const CROSSFADE_MS = 600;
const PANO_LEAVE_MS = 400;

export interface RenderStageProps {
  guid: string;
  plan: FloorPlan;
  focusRoomId: string | null;
  /** The quick still (its `image` is a URL once saved, a data URL before). */
  preview?: EstimatePreview;
  alt: string;
  /** Tests and the sandbox page: where the status comes from instead of the API. */
  fetcher?: RenderStatusFetcher;
  /**
   * On a phone the strip of pictures and the progress go here, a row under the
   * picture, instead of over it (the card renders this element right under
   * its stage). Without it, or on a wider screen, they dock over the picture.
   */
  dockOutlet?: HTMLElement | null;
  /**
   * The animation over the quick still while the first photoreal picture is
   * on its way (cardStyle.ts WaitEffect; Admin → Settings → Estimate output).
   * Off once a picture is shown, and when the render is done or has failed.
   */
  waitEffect?: WaitEffect;
}

type ActiveId = PictureId | 'preview';

const PLACEHOLDERS: RenderPicture[] = PICTURE_ORDER.map((id) => ({ id, name: id, url: null, thumbUrl: null, width: 1920, height: 1080, done: false }));

/**
 * Two image layers: the one shown and the one fading in over it; a change of
 * `src` preloads then swaps — at once, with no fade, while `hidden` (under the 360).
 */
function useCrossfade(src: string | null, hidden: boolean): { shown: string | null; incoming: string | null } {
  const [shown, setShown] = useState<string | null>(null);
  const [incoming, setIncoming] = useState<string | null>(null);
  const shownRef = useRef<string | null>(null);
  const hiddenRef = useRef(hidden); hiddenRef.current = hidden;
  useEffect(() => {
    if (!src || src === shownRef.current) return undefined;
    let cancelled = false;
    let timer: number | null = null;
    const img = new Image();
    const swap = () => {
      if (cancelled) return;
      if (shownRef.current === null || hiddenRef.current) { shownRef.current = src; setShown(src); setIncoming(null); return; }
      setIncoming(src);
      timer = window.setTimeout(() => {
        if (cancelled) return;
        shownRef.current = src;
        setShown(src);
        setIncoming(null);
      }, CROSSFADE_MS);
    };
    img.onload = swap;
    img.onerror = swap; // let the <img> show its own failure rather than hang on the old picture
    img.src = src;
    if (img.complete && img.naturalWidth > 0) swap();
    return () => { cancelled = true; if (timer !== null) window.clearTimeout(timer); };
  }, [src]);
  return { shown, incoming };
}

export const RenderStage: React.FC<RenderStageProps> = ({ guid, plan, focusRoomId, preview, alt, fetcher, dockOutlet, waitEffect = 'none' }) => {
  const { t } = useTranslation('results');
  const phone = usePhoneSized();
  const { status, error, checking, delayed } = useRenderStatus(guid, true, fetcher);
  const job = status?.job ?? null;
  const pictures = status?.pictures?.length ? status.pictures : PLACEHOLDERS;
  const previewUrl = preview?.image ?? status?.preview ?? null;

  const [activeId, setActiveId] = useState<ActiveId>('preview');
  const [userPicked, setUserPicked] = useState(false);
  const [mode, setMode] = useState<'still' | 'pano'>('still');
  const [panoPhase, setPanoPhase] = useState<'enter' | 'ready' | 'leave'>('enter');
  // The still under the 360 (and the one "Back to photos" returns to).
  const [lastStill, setLastStill] = useState<ActiveId>('preview');
  const leaveTimer = useRef<number | null>(null);
  const leaving = useRef<HTMLImageElement | null>(null);

  const pictureOf = useCallback((id: ActiveId) => pictures.find((p) => p.id === id), [pictures]);
  const stillUrl = activeId === 'preview' || activeId === '360'
    ? (activeId === '360' ? (pictureOf(lastStill)?.url ?? previewUrl) : previewUrl)
    : (pictureOf(activeId)?.url ?? previewUrl);
  const { shown, incoming } = useCrossfade(stillUrl, mode === 'pano');

  // The first picture in (never the 360) takes over from the still, once, unless the user chose already.
  useEffect(() => {
    if (userPicked || activeId !== 'preview') return;
    const first = PICTURE_ORDER.find((id) => id !== '360' && pictureOf(id)?.done && pictureOf(id)?.url);
    if (first) { setActiveId(first); setLastStill(first); }
  }, [pictures, userPicked, activeId, pictureOf]);

  useEffect(() => () => {
    if (leaveTimer.current !== null) window.clearTimeout(leaveTimer.current);
    if (leaving.current) leaving.current.onload = leaving.current.onerror = null;
  }, []);

  const leavePano = useCallback((then?: PictureId) => {
    const fade = () => {
      setPanoPhase('leave');
      if (leaveTimer.current !== null) window.clearTimeout(leaveTimer.current);
      leaveTimer.current = window.setTimeout(() => {
        setMode('still');
        setPanoPhase('enter');
        setActiveId(then ?? lastStill);
      }, PANO_LEAVE_MS);
    };
    const url = then ? pictureOf(then)?.url : null;
    if (!then || then === lastStill || !url) { if (then) setLastStill(then); fade(); return; }
    // Another still: load it, put it under the 360 (no fade — it is hidden), then fade the 360 off it.
    if (leaving.current) leaving.current.onload = leaving.current.onerror = null;
    const img = new Image();
    leaving.current = img;
    const go = () => { if (leaving.current !== img) return; leaving.current = null; setLastStill(then); fade(); };
    img.onload = go;
    img.onerror = go;
    img.src = url;
    if (img.complete && img.naturalWidth > 0) go();
  }, [lastStill, pictureOf]);

  const pick = useCallback((id: PictureId) => {
    setUserPicked(true);
    if (id === '360') {
      if (mode === 'pano') return;
      if (activeId !== '360' && activeId !== 'preview') setLastStill(activeId);
      setActiveId('360');
      setPanoPhase('enter');
      setMode('pano');
      return;
    }
    if (mode === 'pano') { leavePano(id); return; }
    setLastStill(id);
    setActiveId(id);
  }, [mode, activeId, leavePano]);

  const pano = pictureOf('360');
  const markers: OpeningMarker[] = useMemo(
    () => (status?.panorama ? openingMarkers(plan, focusRoomId, status.panorama) : []),
    [plan, focusRoomId, status?.panorama],
  );
  const labels: PanoLabels = useMemo(() => ({
    kind: { window: t('render.marker.window'), door: t('render.marker.door'), garageDoor: t('render.marker.garageDoor') },
    dims: (w: number, h: number) => t('render.marker.dims', { width: formatFeetInches(w), height: formatFeetInches(h) }),
    loading: t('render.panoLoading'),
    failed: t('render.panoFailed'),
    openPicture: t('render.openPicture'),
  }), [t]);

  const done = pictures.filter((p) => p.done).length;
  const renderingId = job?.status === 'rendering' && job.picture ? job.picture.id : null;
  const showDock = !!job && error !== 'gone';
  const showProgress = showDock && job.status !== 'done';
  // Waiting on the first picture: the still is what is shown and the render is still to come.
  const waiting = waitEffect !== 'none' && activeId === 'preview' && mode === 'still' && !!job && job.status !== 'done' && job.status !== 'failed';
  const renderedIn = job?.status === 'done' ? durationText(t, job.durationSec) : null;

  return (
    <div className={`rstage${mode === 'pano' ? ' rstage-pano' : ''}${waiting ? ` rstage-wait-${waitEffect}` : ''}`} data-active={activeId}>
      {shown && <img className="rstage-img" src={shown} alt={alt} />}
      {incoming && <img className="rstage-img rstage-img-in" src={incoming} alt="" style={{ animationDuration: `${CROSSFADE_MS}ms` }} />}
      {waiting && <div className="rstage-wait" aria-hidden="true" />}

      {mode === 'pano' && pano?.url && (
        <div className={`rstage-pano-layer is-${panoPhase}`}>
          <PanoViewer src={pano.url} markers={markers} labels={labels} onReady={() => setPanoPhase('ready')} />
          <button type="button" className="rstage-back" onClick={() => leavePano()}>
            {t('render.backToPhotos')}
          </button>
        </div>
      )}

      {(showDock || checking) && (() => {
        const below = phone && !!dockOutlet;
        const dock = (
          <div className={`rdock${below ? ' is-below' : ''}`}>
            {(showProgress || checking) && (
              <RenderProgress job={job} done={done} total={pictures.length} delayed={delayed} checking={checking} />
            )}
            {/* The whole set in: how long the render took (the job's own clock, kept with it). */}
            {!showProgress && renderedIn && <div className="rdock-done">{t('render.progress.renderedIn', { time: renderedIn })}</div>}
            {showDock && <RenderGallery pictures={pictures} activeId={activeId} renderingId={renderingId} onPick={pick} />}
          </div>
        );
        return below ? createPortal(dock, dockOutlet as HTMLElement) : dock;
      })()}
    </div>
  );
};
